import { Box } from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import type { FieldConfiguration } from "../../../../../../../../../api/types/fieldConfigurationTypes.ts";
import { CalculationType } from "../../../../../../../../../api/types/fieldConfigurationTypes.ts";
import type {
  CustomColumnField,
  DimensionField,
  DraggableFieldType,
  DraggableMetaType,
  FieldWrapper,
  MetricField,
  ReportField,
} from "../../../../../../../../../api/types/svTypes.ts";
import { AreaItemType, DimensionFieldType, MetaItemType } from "../../../../../../../../../api/types/svTypes.ts";
import { useAppDispatch } from "../../../../../store/hooks/useAppDispatch.ts";
import { selectAutoShowItemOptionsId } from "../../../../../store/selectors/currentReportSelectors.ts";
import {
  selectBuilderDetailedView,
  selectConditions,
  selectValues,
} from "../../../../../store/selectors/fieldsStateSelectors.ts";
import { selectDimensions } from "../../../../../store/selectors/metaDataSelectors.ts";
import { fieldsStateActions } from "../../../../../store/thunks/fieldStatesThunks.ts";
import { createCurrentReportAction } from "../../../../../store/utils/storeUtils.ts";
import {
  buildCustomColumnConfigChanges,
  isCustomColumn,
  isDimensionBased,
  isMeasure,
  isMetric,
  newCustomColumnField,
} from "../../../../../utilities/fieldsHelper.ts";
import { getDimensionFields, isMeasureConfigurationValid } from "../../../../../utilities/isConfigurationValid.ts";
import type { FieldItemProps } from "../../../../viewer/common/fields/area-fields/areaFiledItem.types.ts";
import { getMeasureIconSet } from "../../../../viewer/common/fields/field-captions/fieldsCaptionIconsProvider.tsx";
import MeasureItem from "../../../../viewer/common/fields/MeasureItem.tsx";
import type { ShowFieldOptionsSettings } from "../../../../viewer/common/Types.ts";
import { formatMeasureCaption } from "../../../../viewer/common/values/helper.tsx";
import type { CustomColumnData } from "../../../common/fields/custom-field/customColumn.types.ts";
import CustomFieldOptions from "../../../common/fields/custom-field/CustomFieldOptions.tsx";
import CustomFormulaDialog from "../../../common/fields/custom-field/CustomFormulaDialog.tsx";
import NewCustomColumnCalculationSetup from "../../../common/fields/custom-field/NewCustomColumnCalculationSetup.tsx";
import { findExistingCustomFormulaField } from "../../../common/fields/custom-field/utilities/findExistingCustomFormulaField.ts";
import DimensionItem from "../../../common/fields/DimensionItem.tsx";
import DropFieldContainer from "../../../common/fields/DropFieldContainer.tsx";
import FieldOptionPopupContainer from "../../../common/fields/measures/FieldOptionPopupContainer.tsx";
import MeasureFieldOptions from "../../../common/fields/measures/MeasureFieldOptions.tsx";
import MetricFieldOptions from "../../../common/fields/metrics/MetricFieldOptions.tsx";
import { createValueField } from "../../../common/utilities/createFields.ts";
import { extractGuid, extractMeta } from "../../../common/utilities/dropFieldContainerHelper.ts";
import {
  formatDimensionFieldCaption,
  formatDimensionInFieldsCaption,
} from "../../../utils/formatDimensionFieldCaptions.tsx";
import AggregationOptionsComponent from "./AggregationOptionsComponent.tsx";
import DimensionFieldOptions from "./DimensionFieldOptions.tsx";
import ValuesOtherOptions from "./ValuesOtherOptions.tsx";

interface Props {
  fields: ReportField[];
  maxFields?: number;
  canDropItem: (item: FieldWrapper<ReportField> | DraggableMetaType) => boolean;
  canAddCustomField?: boolean;
  readonlyMode: boolean;
}

export const ValuesDropFieldsContainer = ({
  fields,
  maxFields,
  canDropItem,
  canAddCustomField,
  readonlyMode,
}: Props) => {
  const dispatch = useAppDispatch();
  const detailedView = useSelector(selectBuilderDetailedView);
  const conditionsAreaValues = useSelector(selectConditions);
  const valuesAreaValues = useSelector(selectValues);
  const autoShowItemOptionsId = useSelector(selectAutoShowItemOptionsId);

  const dimensions = useSelector(selectDimensions);
  const dimensionsRef = useRef(dimensions);
  useEffect(() => {
    dimensionsRef.current = dimensions;
  }, [dimensions]);

  const [existingCustomFormulaField, setExistingCustomFormulaField] = useState<CustomColumnField | undefined>(
    undefined
  );

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [currentFieldId, setCurrentFieldId] = useState<string>();

  const fieldsAreaRef = useRef(valuesAreaValues);
  useEffect(() => {
    fieldsAreaRef.current = valuesAreaValues;
  }, [valuesAreaValues]);

  const currentField = useMemo(() => {
    if (!currentFieldId) {
      return undefined;
    }
    return valuesAreaValues.find((f) => f.config.guid === currentFieldId);
  }, [currentFieldId, valuesAreaValues]);

  const showFieldOptions = useCallback((settings: ShowFieldOptionsSettings<ReportField>) => {
    setAnchorEl(settings.ref);
    setCurrentFieldId(settings.field.config.guid);
  }, []);

  const measureCaptionColor = useCallback(
    (value: ReportField) => {
      const valid = isMeasureConfigurationValid(conditionsAreaValues, fields, dimensionsRef.current, value);
      return valid.valid ? undefined : "error.main";
    },
    [conditionsAreaValues, fields]
  );

  const formatCaption = useCallback(
    (field: ReportField) => {
      if (isMeasure(field) || isMetric(field) || isCustomColumn(field)) {
        return formatMeasureCaption(field, measureCaptionColor(field), getMeasureIconSet(field, []));
      } else if (isDimensionBased(field)) {
        return formatDimensionFieldCaption(field);
      }
      return undefined;
    },
    [measureCaptionColor]
  );

  const handleCanDropItem = useCallback(
    (item: FieldWrapper<ReportField> | DraggableMetaType) => canDropItem(item),
    [canDropItem]
  );

  const removeItem = useCallback(
    (field: ReportField) => {
      dispatch(fieldsStateActions.removeMeasure(field));
    },
    [dispatch]
  );

  const handleRemove = useCallback(() => {
    setAnchorEl(null);
    if (currentField) {
      removeItem(currentField);
    }
  }, [currentField, removeItem]);

  const closePopup = useCallback(() => {
    setCurrentFieldId(undefined);
    setAnchorEl(null);
  }, []);

  const updateItemConfig = useCallback(
    (field: ReportField, changes: Partial<FieldConfiguration>) => {
      dispatch(fieldsStateActions.updateMeasureConfig({ field, changes }));
    },
    [dispatch]
  );

  const handleUpdateConfiguration = useCallback(
    (changes: Partial<FieldConfiguration>, causeClosing: boolean) => {
      if (!currentField) {
        return;
      }
      updateItemConfig(currentField, changes);
      if (causeClosing) {
        setAnchorEl(null);
      }
    },
    [currentField, updateItemConfig]
  );

  const handleDimensionFieldCustomLabelChange = useCallback(
    (name: string | undefined) => {
      if (!isDimensionBased(currentField)) {
        return;
      }
      dispatch(fieldsStateActions.updateMeasureConfig({ field: currentField, changes: { customLabel: name } }));
    },
    [currentField, dispatch]
  );

  const moveItem = useCallback(
    (field: ReportField, newIndex: number) => {
      dispatch(fieldsStateActions.moveMeasure({ field, newIndex }));
    },
    [dispatch]
  );

  const addItem = useCallback(
    (field: ReportField, index: number) => {
      dispatch(fieldsStateActions.addMeasure({ field, index }));
    },
    [dispatch]
  );

  const handleDimensionFormatCaption = useCallback(
    (dimension: DimensionField | MetricField) => formatDimensionInFieldsCaption(dimension),
    []
  );

  const onSetAutoShowItemOptionsId = useCallback(
    (id?: string) => {
      dispatch(createCurrentReportAction("setAutoShowItemOptionsId", id));
    },
    [dispatch]
  );

  const createItem = useCallback(
    (props: FieldItemProps<ReportField>) => {
      const field = props.field;
      if (isMeasure(field) || isMetric(field) || isCustomColumn(field)) {
        return (
          <MeasureItem
            detailedView={detailedView}
            field={field}
            dimensions={dimensions}
            globalConditions={conditionsAreaValues}
            formatCaption={formatCaption}
            canBeRemoved={true}
            onRemoveItem={removeItem}
            updateMeasure={updateItemConfig}
            onShowOptions={showFieldOptions}
            canToggleParameter={true}
            isReadonlyCustomLabel={true}
            autoShowItemOptionsId={autoShowItemOptionsId}
            onSetAutoShowItemOptionsId={onSetAutoShowItemOptionsId}
            readonly={readonlyMode}
          />
        );
      }
      return (
        <DimensionItem
          detailedView={detailedView}
          field={field}
          dimensions={dimensions}
          globalConditions={conditionsAreaValues}
          canBeRemoved
          onRemoveItem={removeItem}
          formatCaption={handleDimensionFormatCaption}
          updateField={updateItemConfig}
          onShowOptions={showFieldOptions}
          autoShowItemOptionsId={autoShowItemOptionsId}
          onSetAutoShowItemOptionsId={onSetAutoShowItemOptionsId}
          readonly={readonlyMode}
        />
      );
    },
    [
      detailedView,
      conditionsAreaValues,
      formatCaption,
      removeItem,
      updateItemConfig,
      showFieldOptions,
      handleDimensionFormatCaption,
      autoShowItemOptionsId,
      onSetAutoShowItemOptionsId,
      dimensions,
      readonlyMode,
    ]
  );

  const [addCustomFieldData, setAddCustomFieldData] = useState<{
    calculationType: CalculationType;
    fieldGuid: string | undefined;
    anchorEl: HTMLElement | null;
  } | null>(null);

  const handleCalculationItemClick = (calculationType: CalculationType) => {
    setAddCustomFieldData({ calculationType, fieldGuid: currentField?.config.guid, anchorEl: anchorEl });
    setExistingCustomFormulaField(findExistingCustomFormulaField(valuesAreaValues, currentField?.config.guid));
    closePopup();
  };

  const handleApplyCustomCalculation = (data: CustomColumnData) => {
    if (existingCustomFormulaField) {
      updateItemConfig(existingCustomFormulaField, buildCustomColumnConfigChanges(data));
      setAddCustomFieldData(null);
      return;
    }

    const field = newCustomColumnField(data);
    addItem(createValueField(field.meta), valuesAreaValues.length);
    setAddCustomFieldData(null);
  };

  const handleEditCustomFormulaCalculationField = (fieldGuid: string) => {
    setAddCustomFieldData({ calculationType: CalculationType.CustomFormula, fieldGuid, anchorEl: anchorEl });
    setExistingCustomFormulaField(findExistingCustomFormulaField(valuesAreaValues, fieldGuid));
    closePopup();
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: ".5rem" }}>
      <DropFieldContainer
        title="Values"
        maxFields={maxFields}
        areaFieldType="VALUES"
        acceptedDropTypes={[
          AreaItemType.MEASURE,
          AreaItemType.METRIC,
          AreaItemType.DIMENSION,
          AreaItemType.COLUMNS,
          AreaItemType.ROWS,
          AreaItemType.CONDITIONS,
          MetaItemType.AGGREGATION,
        ]}
        fields={valuesAreaValues}
        canDropItem={handleCanDropItem}
        renderListItem={createItem}
        getKeyValue={getKeyValue}
        formatCaption={formatCaption}
        onAddItem={addItem}
        onRemoveItem={removeItem}
        onMoveItem={moveItem}
        createListItem={createValueField}
        canAddCustomField={canAddCustomField}
        readonly={readonlyMode}
      />
      {addCustomFieldData && (
        <>
          {addCustomFieldData.calculationType === CalculationType.PercentageOfTotal && (
            <FieldOptionPopupContainer
              anchorEl={addCustomFieldData.anchorEl}
              cancel={() => setAddCustomFieldData(null)}
            >
              <NewCustomColumnCalculationSetup
                fieldGuid={addCustomFieldData.fieldGuid}
                calculationType={addCustomFieldData.calculationType}
                onClose={() => setAddCustomFieldData(null)}
                onApply={handleApplyCustomCalculation}
                readonly={readonlyMode}
              />
            </FieldOptionPopupContainer>
          )}
          {addCustomFieldData.calculationType === CalculationType.CustomFormula && (
            <CustomFormulaDialog
              open
              onClose={() => setAddCustomFieldData(null)}
              onApply={handleApplyCustomCalculation}
              editedField={existingCustomFormulaField}
              sourceFieldGuid={addCustomFieldData.fieldGuid}
            />
          )}
        </>
      )}
      <FieldOptionPopupContainer anchorEl={anchorEl} cancel={closePopup}>
        {currentField?.type === DimensionFieldType.DIMENSION && (
          <DimensionFieldOptions
            field={currentField}
            AggregationOptions={
              <AggregationOptionsComponent
                field={currentField}
                saveChanges={handleUpdateConfiguration}
                readonly={readonlyMode}
              />
            }
            cancel={closePopup}
            onRemove={handleRemove}
            onUpdateConfiguration={handleUpdateConfiguration}
            onUpdateCustomLabel={handleDimensionFieldCustomLabelChange}
            onCalculationItemClick={handleCalculationItemClick}
            canAddCustomField={canAddCustomField}
            readonly={readonlyMode}
          />
        )}
        {currentField?.type === DimensionFieldType.MEASURE && (
          <MeasureFieldOptions
            field={currentField}
            dimensionFields={getDimensionFields(fields)}
            conditions={conditionsAreaValues}
            otherOptions={
              <ValuesOtherOptions
                field={currentField}
                saveChanges={handleUpdateConfiguration}
                readonly={readonlyMode}
              />
            }
            cancel={closePopup}
            saveChanges={handleUpdateConfiguration}
            onRemove={handleRemove}
            onCalculationItemClick={handleCalculationItemClick}
            canAddCustomField={canAddCustomField}
            readonly={readonlyMode}
          />
        )}
        {currentField?.type === DimensionFieldType.METRIC && (
          <MetricFieldOptions
            field={currentField}
            otherOptions={
              <ValuesOtherOptions
                field={currentField}
                saveChanges={handleUpdateConfiguration}
                readonly={readonlyMode}
              />
            }
            cancel={closePopup}
            saveChanges={handleUpdateConfiguration}
            onRemove={handleRemove}
            readonly={readonlyMode}
          />
        )}
        {currentField?.type === DimensionFieldType.CUSTOM && (
          <CustomFieldOptions
            field={currentField}
            otherOptions={
              <ValuesOtherOptions
                field={currentField}
                saveChanges={handleUpdateConfiguration}
                readonly={readonlyMode}
              />
            }
            cancel={closePopup}
            saveChanges={handleUpdateConfiguration}
            onRemove={handleRemove}
            onEditCustomFormulaCalculationField={handleEditCustomFormulaCalculationField}
            readonly={readonlyMode}
          />
        )}
      </FieldOptionPopupContainer>
    </Box>
  );
};

function getKeyValue(field: FieldWrapper<DraggableFieldType> | DraggableMetaType) {
  const guid = extractGuid(field);
  if (guid) {
    return guid;
  }
  const meta = extractMeta(field);
  if (meta) {
    return meta.name;
  }
  return "";
}

export default ValuesDropFieldsContainer;
